/**
 * 全局音频播放条（与根节点 `CustomAudioPlaybackController` 联动）
 * @module store/modules/globalAudioPlayback
 */

const DEFAULT_PLACEHOLDER = '语音合成中，请稍后';

const initialState = () => ({
  /** 是否展示控制条（`v-show`，实例不销毁） */
  visible: false,
  /** 初始化占位态（语音合成中等） */
  initializing: false,
  /** 占位条文案 */
  placeholderText: DEFAULT_PLACEHOLDER,
  /** 音频 URL 列表 */
  srcList: [],
  /** 当前曲目下标，从第几个开始播放 */
  trackCurrent: 0,
  /** 当前音频是否正在播放 */
  playing: false,
  /** 总曲数 */
  totalTracks: 0,
  /** 曲目标签 */
  label: '',
  /** 音频来源类型，用于业务侧识别当前播放内容 */
  sourceType: '',
  /** 音频来源 ID，用于业务侧识别当前播放内容 */
  sourceId: '',
  /** 是否自动播放 */
  autoPlay: true,
  /** 播放完是否自动播放下一首 */
  autoNextPlay: true,
  /** 外部拉流等加载态 */
  fetchAudioLoading: false
});

const isEmpty = (value) => typeof value === 'undefined' || value === null || value === '';

/**
 * 由 `INIT` payload 得到应写入模块 state 的片段（不替换整个 state 引用）。
 * @param {object} p - 与 mutation `INIT` 的 payload 一致
 * @returns {object}
 */
const buildInitPatch = (p) => {
  const patch = {
    visible: isEmpty(p.visible) ? true : p.visible,
    initializing: isEmpty(p.initializing) ? false : p.initializing,
    placeholderText: isEmpty(p.placeholderText) ? DEFAULT_PLACEHOLDER : p.placeholderText,
    fetchAudioLoading: isEmpty(p.fetchAudioLoading) ? false : p.fetchAudioLoading,
    autoNextPlay: isEmpty(p.autoNextPlay) ? true : p.autoNextPlay,
    autoPlay: isEmpty(p.autoPlay) ? true : p.autoPlay,
    label: isEmpty(p.label) ? '' : p.label,
    sourceType: isEmpty(p.sourceType) ? '' : p.sourceType,
    sourceId: isEmpty(p.sourceId) ? '' : String(p.sourceId),
    playing: false
  };
  if (Array.isArray(p.srcList)) {
    patch.srcList = p.srcList;
    patch.totalTracks = Math.max(1, p.srcList.length);
  }
  if (typeof p.trackCurrent === 'number' && !Number.isNaN(p.trackCurrent)) {
    const t = Math.floor(p.trackCurrent);
    patch.trackCurrent = t < 0 ? 0 : t;
  }
  if (typeof p.totalTracks === 'number' && !Number.isNaN(p.totalTracks)) {
    patch.totalTracks = Math.max(1, Math.floor(p.totalTracks));
  }
  return patch;
};

export default {
  namespaced: true,
  state: initialState(),
  getters: {},
  mutations: {
    /**
     * 初始化或更新展示与播放相关字段（合并 payload，未传字段保持原值）。
     * @param {object} state - 模块 state
     * @param {object} [payload]
     * @param {boolean} [payload.visible]
     * @param {boolean} [payload.initializing]
     * @param {string} [payload.placeholderText]
     * @param {string[]} [payload.srcList]
     * @param {number} [payload.trackCurrent] - 列表默认下标从 0 起
     * @param {number} [payload.totalTracks]
     * @param {string} [payload.label] - 曲目标签
     * @param {string} [payload.sourceType] - 音频来源类型
     * @param {string|number} [payload.sourceId] - 音频来源 ID
     * @param {boolean} [payload.fetchAudioLoading]
     * @param {boolean} [payload.autoNextPlay] - 播放完是否自动播放下一首
     * @param {boolean} [payload.autoPlay] - 是否自动播放
     */
    INIT(state, payload = {}) {
      Object.assign(state, buildInitPatch(payload));
    },
    /**
     * 在列表末尾追加 URL（过滤空串）。
     * @param {string[]|string} urls
     */
    APPEND_SRC_LIST(state, urls) {
      const raw = Array.isArray(urls) ? urls : [urls];
      state.srcList = state.srcList.concat(raw);
      state.totalTracks = Math.max(1, state.srcList.length);
    },
    /**
     * 同步播放器内部当前曲目下标。
     * @param {object} state - 模块 state
     * @param {number} trackIndex - 当前曲目下标，从 0 起
     */
    SET_TRACK_CURRENT(state, trackIndex = 0) {
      if (typeof trackIndex !== 'number' || Number.isNaN(trackIndex)) return;
      state.trackCurrent = Math.max(0, Math.floor(trackIndex));
    },
    /**
     * 同步播放器播放态。
     * @param {object} state - 模块 state
     * @param {boolean} playing - 是否正在播放
     */
    SET_PLAYING(state, playing = false) {
      state.playing = playing;
    },
    /**
     * 关闭条：隐藏 UI，结束占位/拉流加载态。
     */
    CLOSE(state) {
      const next = initialState();
      Object.keys(next).forEach((key) => {
        state[key] = next[key];
      });
    },
    /**
     * 设置初始化态
     * @param {boolean} initializing
     */
    SET_INITIALIZING(state, initializing = false) {
      state.visible = true;
      state.initializing = initializing;
    }
  },
  actions: {
    /**
     * 初始化或更新全局音频条状态。
     * @param {object} [payload] - 同 mutation `INIT`
     */
    init({ commit }, payload = {}) {
      commit('INIT', payload);
      return true;
    },
    /**
     * 向 `srcList` 末尾追加地址。
     * @param {string[]|string} urls
     */
    appendSrcList({ commit }, urls) {
      commit('APPEND_SRC_LIST', urls);
    },
    /**
     * 同步当前曲目下标。
     * @param {object} context
     * @param {Function} context.commit - Vuex commit
     * @param {number} trackIndex - 当前曲目下标，从 0 起
     */
    syncTrackCurrent({ commit }, trackIndex) {
      commit('SET_TRACK_CURRENT', trackIndex);
    },
    /**
     * 同步播放态。
     * @param {object} context
     * @param {Function} context.commit - Vuex commit
     * @param {boolean} playing - 是否正在播放
     */
    syncPlaying({ commit }, playing) {
      commit('SET_PLAYING', playing);
    },
    /**
     * 隐藏全局音频条（并清理占位/拉流加载标记）。
     */
    close({ commit }) {
      commit('CLOSE');
    },
    /**
     * 切换初始化态
     * @param {boolean} initializing
     */
    toggleInitializing({ commit }, initializing) {
      commit('SET_INITIALIZING', initializing);
    }
  }
};
